import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ProductManagers from "@/pages/ProductManagers";

function ContactDirectory({ kind, endpoint, label, testidPrefix, isReadOnly }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [pageItems, ctrl] = usePagination(items, 10);
  const [confirmDel, setConfirmDel] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => setItems((await api.get(endpoint)).data), [endpoint]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit) await api.patch(`${endpoint}/${edit.id}`, form);
      else await api.post(endpoint, form);
      toast.success(edit ? `${label} diperbarui` : `${label} ditambahkan`);
      setForm({ name: "", company: "", email: "", phone: "" });
      setEdit(null);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success(`${label} dihapus`);
      load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      return false;
    }
  };
  const doConfirmDel = async () => {
    if (!confirmDel) return;
    if (await remove(confirmDel.id)) setConfirmDel(null);
  };
  const openEdit = (item) => {
    setEdit(item);
    setForm({ name: item.name || "", company: item.company || "", email: item.email || "", phone: item.phone || "" });
  };

  return (
    <>
      {!isReadOnly && (
      <form onSubmit={submit} className="card-tech p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div><Label className="label-mini">Nama</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${testidPrefix}-name-input`} /></div>
        <div><Label className="label-mini">Perusahaan</Label>
          <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid={`${testidPrefix}-company-input`} /></div>
        <div><Label className="label-mini">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`${testidPrefix}-email-input`} /></div>
        <div><Label className="label-mini">Telp</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid={`${testidPrefix}-phone-input`} /></div>
        <div className="md:col-span-4">
          <Button type="submit" className="rounded-sm" data-testid={`add-${testidPrefix}-button`}><Plus className="h-4 w-4 mr-2" />{edit ? "Simpan Perubahan" : "Tambah"}</Button>
        </div>
      </form>
      )}

      <div className="card-tech p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-6 py-3">Nama</th>
              <th className="text-left px-6 py-3">Perusahaan</th>
              <th className="text-left px-6 py-3">Email</th>
              <th className="text-left px-6 py-3">Telp</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">Belum ada {kind.toLowerCase()}.</td></tr>}
            {pageItems.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`${testidPrefix}-row-${it.id}`}>
                <td className="px-6 py-3 font-medium">{it.name}</td>
                <td className="px-6 py-3 text-muted-foreground">{it.company}</td>
                <td className="px-6 py-3 mono text-xs">{it.email}</td>
                <td className="px-6 py-3 mono text-xs">{it.phone}</td>
                <td className="px-6 py-3 text-right">
                  {!isReadOnly && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-${testidPrefix}-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDel(it)} data-testid={`delete-${testidPrefix}-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination ctrl={ctrl} testidPrefix={`${testidPrefix}-pg`} />
      </div>
      <ConfirmDelete
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doConfirmDel}
        entityLabel={confirmDel?.name}
        testIdPrefix={`confirm-delete-${testidPrefix}`}
      />
    </>
  );
}

export default function Clients() {
  const { isReadOnly } = useAuth();
  return (
    <div className="space-y-8" data-testid="clients-page">
      <div>
        <div className="label-mini">Client, Partner & Product Manager</div>
        <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Direktori Relasi & PIC</h1>
      </div>
      <Tabs defaultValue="clients">
        <TabsList>
          <TabsTrigger value="clients" data-testid="clients-tab">Client</TabsTrigger>
          <TabsTrigger value="partners" data-testid="partners-tab">Partner</TabsTrigger>
          <TabsTrigger value="product-managers" data-testid="product-managers-tab">Product Manager</TabsTrigger>
        </TabsList>
        <TabsContent value="clients" className="mt-6">
          <ContactDirectory kind="Client" endpoint="/clients" label="Client" testidPrefix="client" isReadOnly={isReadOnly} />
        </TabsContent>
        <TabsContent value="partners" className="mt-6">
          <ContactDirectory kind="Partner" endpoint="/partners" label="Partner" testidPrefix="partner" isReadOnly={isReadOnly} />
        </TabsContent>
        <TabsContent value="product-managers" className="mt-6">
          <ProductManagers embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
